import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, Lock, ArrowRight, Eye, EyeOff, Fingerprint, Check } from "lucide-react";
import { GoogleAuthButton } from "@/components/auth/GoogleAuthButton";
import { AppleAuthButton } from "@/components/auth/AppleAuthButton";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { getPendingInvite } from "@/lib/pendingInvite";
import { getIntendedPlan } from "@/lib/intendedPlan";
import {
  biometryLabel,
  checkBiometricAvailability,
  clearBiometricCredentials,
  getRememberedEmail,
  getStoredCredentials,
  isBiometricEnabled,
  setRememberedEmail,
  storeCredentials,
  verifyBiometric,
} from "@/lib/biometric";

// localStorage flag so the "Enable Face ID?" one-shot prompt is shown
// at most once per device. Survives logout (different account on the
// same device → no need to re-ask). Reset by clearing app data.
const KEY_BIO_PROMPT_SEEN = "kinly.biom.prompt.seen";

export default function SignIn({ defaultMode = "signup" }) {
  const navigate = useNavigate();
  const { login, signup } = useAuth();
  const [mode, setMode] = useState(defaultMode); // signup | login
  const [form, setForm] = useState({ email: "", password: "", display_name: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pendingInviteEmail, setPendingInviteEmail] = useState(null);
  const [bio, setBio] = useState({ available: false, biometryType: "none", enabled: false });
  // One-shot post-signin "Enable Face ID?" prompt. Stored after a
  // successful password sign-in so we can use the password the user
  // JUST entered to set up biometrics without re-asking.
  const [bioPrompt, setBioPrompt] = useState(null);  // null | { email, password }
  const [bioPromptBusy, setBioPromptBusy] = useState(false);
  // Surface the pre-signup plan choice as a soft pill — reinforces the
  // commitment so the user doesn't feel like they wasted that step.
  const intendedPlan = mode === "signup" ? getIntendedPlan() : null;

  // Honour a pending family-invite (sessionStorage hint from EmailInviteAccept):
  // pre-fill email so the invitee doesn't accidentally type the wrong address.
  // This is a UX nicety only — the BACKEND will recognise the invite by email
  // even if this hint isn't present (e.g., they signed up from TestFlight).
  useEffect(() => {
    const { token, email } = getPendingInvite();
    if (token && email) {
      setPendingInviteEmail(email);
      setForm((s) => s.email ? s : { ...s, email });
      return;
    }
    // Otherwise, pre-fill from "remember me" if we have it.
    const remembered = getRememberedEmail();
    if (remembered) {
      setForm((s) => s.email ? s : { ...s, email: remembered });
      setRemember(true);
    }
  }, []);

  // Check biometric availability on mount so we can render the Face ID
  // shortcut button when the user previously opted in.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const a = await checkBiometricAvailability();
      const enabled = isBiometricEnabled();
      if (!cancelled) setBio({ ...a, enabled });
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-fire Face ID / Touch ID immediately when a returning user opens
  // the app — they shouldn't have to tap a button. Guarded by a ref so
  // React StrictMode dev-mode double-mount doesn't double-prompt, and
  // by `mode === "login"` so we don't fire on signup or invite-accept
  // routes. Falls through to the manual button if the user cancels.
  const bioAutoFiredRef = React.useRef(false);
  useEffect(() => {
    if (bioAutoFiredRef.current) return;
    if (mode !== "login") return;
    if (pendingInviteEmail) return;       // invite flow needs explicit creds
    if (!bio.available || !bio.enabled) return;
    bioAutoFiredRef.current = true;
    // Defer one tick so the form has a chance to paint — avoids a flash
    // of the password fields before Face ID takes over the screen.
    const t = setTimeout(() => { onBiometricSignIn(); }, 250);
    return () => clearTimeout(t);
  }, [bio.available, bio.enabled, mode, pendingInviteEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  // Resolve the post-auth destination by hitting the backend invite-list.
  // This is the source of truth, regardless of which device the email
  // was opened on or whether sessionStorage has the hint.
  const resolveNextRoute = async () => {
    try {
      const { data } = await api.get("/me/pending-invites");
      const invs = Array.isArray(data?.invites) ? data.invites : [];
      if (invs.length > 0) return `/i/accept/${invs[0].accept_token}`;
    } catch { /* fall through */ }
    return null;
  };

  const doLogin = async ({ email, password }) => {
    await login({ email, password });
    // Persist "remember me" preference — email only, no password.
    setRememberedEmail(remember ? email : "");
    // If biometric is available on the device, the user hasn't enabled
    // it yet AND hasn't dismissed the one-shot prompt before, ask now.
    // We keep the password in scope so a "Yes" can wire it up without a
    // second password challenge.
    try {
      const seen = localStorage.getItem(KEY_BIO_PROMPT_SEEN) === "1";
      const avail = bio.available && bio.biometryType !== "none";
      if (!seen && avail && !isBiometricEnabled()) {
        setBioPrompt({ email, password });
        return;  // navigation deferred until prompt is dismissed
      }
    } catch { /* localStorage disabled — skip prompt and just navigate */ }
    const inviteRoute = await resolveNextRoute();
    navigate(inviteRoute || "/today");
  };

  // Finishes the deferred sign-in navigation (used by both Yes/No paths
  // of the biometric prompt). Marks the prompt as "seen" so it never
  // re-appears on this device.
  const finishSigninAfterBioPrompt = async () => {
    try { localStorage.setItem(KEY_BIO_PROMPT_SEEN, "1"); } catch { /* noop */ }
    const inviteRoute = await resolveNextRoute();
    navigate(inviteRoute || "/today");
  };

  const onBioPromptYes = async () => {
    if (!bioPrompt) return;
    setBioPromptBusy(true);
    const r = await verifyBiometric({ reason: "Enable Face ID sign-in for Kinly" });
    if (r.ok) {
      storeCredentials({ email: bioPrompt.email, password: bioPrompt.password });
    }
    setBioPromptBusy(false);
    setBioPrompt(null);
    await finishSigninAfterBioPrompt();
  };

  const onBioPromptSkip = async () => {
    setBioPrompt(null);
    await finishSigninAfterBioPrompt();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      if (mode === "signup") {
        await signup(form);
        setRememberedEmail(remember ? form.email : "");
        const inviteRoute = await resolveNextRoute();
        navigate(inviteRoute || "/onboarding/verify-email");
      } else {
        await doLogin({ email: form.email, password: form.password });
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      const msg = formatApiError(detail) || err.message || "Something went wrong.";
      setError(status ? `${msg} (${status})` : msg);
    } finally {
      setLoading(false);
    }
  };

  const onBiometricSignIn = async () => {
    setError("");
    const result = await verifyBiometric({ reason: "Sign in to Kinly" });
    if (!result.ok) {
      setError(result.message || "Biometric sign-in was cancelled.");
      return;
    }
    const { email, password } = getStoredCredentials();
    if (!email || !password) {
      // Stored creds vanished (e.g. user cleared app data) — clear flag and tell them.
      clearBiometricCredentials();
      setBio((b) => ({ ...b, enabled: false }));
      setError("Saved credentials are missing. Please sign in once with your password.");
      return;
    }
    setLoading(true);
    try {
      await doLogin({ email, password });
    } catch (err) {
      // Password may have changed since we stored it. Clear and ask to re-auth.
      clearBiometricCredentials();
      setBio((b) => ({ ...b, enabled: false }));
      const detail = err.response?.data?.detail;
      const msg = formatApiError(detail) || "Saved sign-in is no longer valid. Please sign in with your password.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const showBiometricButton =
    mode === "login" && bio.available && bio.enabled && !pendingInviteEmail;
  const bioLabel = biometryLabel(bio.biometryType);

  return (
    <div className="kinly-canvas kinly-onboarding-bg">
      <div
        className="absolute inset-0 overflow-y-auto px-6 pt-safe flex flex-col"
        style={{
          // Reserve space so the keyboard never covers the submit button
          // when an input is focused. `--kb` is the live keyboard height
          // (lib/keyboard.js); collapses to safe-area-inset-bottom when
          // the keyboard is closed.
          paddingBottom: "calc(var(--kb, 0px) + env(safe-area-inset-bottom) + 24px)",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {/* Logo wordmark */}
        <div className="pt-14 mb-auto">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-display font-semibold">
              k
            </div>
            <span className="font-display text-xl font-medium tracking-tight">
              Kinly
            </span>
          </div>
          <p className="text-xs text-muted-foreground tracking-wide uppercase">
            by Geminii Space
          </p>
        </div>

        {/* Hero */}
        <div className="mb-10">
          <h1
            data-testid="signin-hero"
            className="font-display text-[34px] leading-[1.1] font-medium tracking-tight"
          >
            Welcome to Kinly.
            <br />
            <span className="text-muted-foreground">
              Your family's quiet co-pilot.
            </span>
          </h1>
        </div>

        {pendingInviteEmail && (
          <div
            data-testid="signin-invite-banner"
            className="mb-6 rounded-2xl p-4 border border-primary/30 bg-primary/[0.06]"
          >
            <p className="text-[12px] uppercase tracking-[0.18em] text-primary font-medium">
              You're accepting a family invite
            </p>
            <p className="text-[14px] mt-1 leading-relaxed">
              {mode === "signup"
                ? <>Create your Kinly account with <span className="font-display">{pendingInviteEmail}</span> — you'll be added to the family after you verify your email.</>
                : <>Sign in with <span className="font-display">{pendingInviteEmail}</span> to join.</>}
            </p>
          </div>
        )}

        {!pendingInviteEmail && intendedPlan && intendedPlan.tier_key && intendedPlan.tier_key !== "free" && (
          <div
            data-testid="signin-plan-pill"
            className="mb-6 inline-flex items-center gap-2 rounded-full px-3 py-1.5 bg-primary/[0.08] border border-primary/20 self-start"
          >
            <Check className="w-3.5 h-3.5 text-primary" strokeWidth={2.5} />
            <span className="text-[12px] text-primary font-medium">
              Continuing with Kinly {intendedPlan.tier_key[0].toUpperCase()}{intendedPlan.tier_key.slice(1)}
            </span>
          </div>
        )}

        {showBiometricButton && (
          <button
            data-testid="signin-biometric"
            type="button"
            onClick={onBiometricSignIn}
            disabled={loading}
            className="mb-4 w-full h-14 rounded-2xl border-2 border-primary text-primary font-medium tap flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Fingerprint className="w-5 h-5" />
            Sign in with {bioLabel}
          </button>
        )}

        {/* Google + Apple Sign-In — Emergent-managed Google OAuth + native
            Apple Sign-In. Shown for both signup and login modes. Hidden when
            the user is mid-invite (their email is already locked to a
            specific address). */}
        {!pendingInviteEmail && (
          <>
            <div className="space-y-3">
              <GoogleAuthButton
                disabled={loading}
                label={mode === "signup" ? "Sign up with Google" : "Continue with Google"}
              />
              <AppleAuthButton
                disabled={loading}
                label={mode === "signup" ? "Sign up with Apple" : "Continue with Apple"}
              />
            </div>
            <div
              data-testid="signin-or-divider"
              className="my-5 flex items-center gap-3 text-[11px] uppercase tracking-[0.18em] text-muted-foreground"
            >
              <div className="flex-1 h-px bg-border" />
              <span>or</span>
              <div className="flex-1 h-px bg-border" />
            </div>
          </>
        )}

        {/* Form */}
        <form onSubmit={onSubmit} className="space-y-4">
          {mode === "signup" && (
            <Field
              label="Your name"
              testid="signin-name"
              type="text"
              value={form.display_name}
              onChange={update("display_name")}
              placeholder="Sarah"
              autoComplete="name"
            />
          )}
          <Field
            label="Email"
            testid="signin-email"
            type="email"
            icon={Mail}
            value={form.email}
            onChange={update("email")}
            placeholder="you@email.com"
            autoComplete="email"
          />
          <PasswordField
            label="Password"
            testid="signin-password"
            icon={Lock}
            value={form.password}
            onChange={update("password")}
            placeholder="At least 8 characters"
            autoComplete={mode === "signup" ? "new-password" : "current-password"}
            visible={showPassword}
            onToggleVisible={() => setShowPassword((v) => !v)}
          />

          {mode === "login" && (
            <RememberMeCheckbox checked={remember} onChange={() => setRemember((v) => !v)} />
          )}

          {error && (
            <p
              data-testid="signin-error"
              className="text-sm text-destructive bg-destructive/10 rounded-lg p-2"
            >
              {error}
            </p>
          )}

          <button
            data-testid="signin-submit"
            type="submit"
            disabled={loading}
            className="w-full h-14 rounded-2xl bg-foreground text-background font-medium tap flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? "One sec…" : mode === "signup" ? "Get started" : "Sign in"}
            {!loading && <ArrowRight className="w-4 h-4" />}
          </button>

          <button
            data-testid="signin-toggle-mode"
            type="button"
            onClick={() => {
              setMode(mode === "signup" ? "login" : "signup");
              setError("");
            }}
            className="w-full text-sm text-muted-foreground py-2"
          >
            {mode === "signup"
              ? "Already have an account? Sign in"
              : "New here? Create an account"}
          </button>
        </form>

        <p className="text-[11px] text-muted-foreground text-center mt-6 leading-relaxed">
          Magic-link sign-in arrives in the next build.
          <br />
          By continuing you agree to our terms.
        </p>
      </div>

      {/* One-shot biometric opt-in — shown once after a successful
          password sign-in if Face ID / Touch ID is available on the
          device. Uses the password the user just entered, so "Yes"
          completes setup in a single Face ID confirmation. */}
      {bioPrompt && (
        <div
          data-testid="bio-prompt-sheet"
          className="fixed inset-0 z-[80] flex items-center justify-center bg-foreground/40 backdrop-blur-sm p-4"
        >
          <div className="w-full max-w-[400px] bg-background rounded-3xl p-6 shadow-2xl">
            <div className="w-14 h-14 rounded-full bg-primary/15 text-primary mx-auto mb-5 flex items-center justify-center">
              <Fingerprint className="w-7 h-7" />
            </div>
            <h2 className="font-display text-[22px] font-medium tracking-tight text-center">
              Use {biometryLabel(bio.biometryType)} next time?
            </h2>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed text-center">
              Skip the password — sign in with a glance. You can change this anytime in Settings.
            </p>
            <button
              data-testid="bio-prompt-yes"
              disabled={bioPromptBusy}
              onClick={onBioPromptYes}
              className="w-full h-12 rounded-2xl bg-primary text-primary-foreground font-medium tap mt-6 inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Fingerprint className="w-4 h-4" />
              {bioPromptBusy ? "Setting up…" : `Enable ${biometryLabel(bio.biometryType)}`}
            </button>
            <button
              data-testid="bio-prompt-skip"
              disabled={bioPromptBusy}
              onClick={onBioPromptSkip}
              className="w-full h-11 text-sm text-muted-foreground tap mt-2"
            >
              Not now
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, icon: Icon, testid, ...rest }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium">
        {label}
      </span>
      <div className="mt-1 flex items-center gap-2 border-b-2 border-border focus-within:border-primary transition-colors py-2">
        {Icon && <Icon className="w-4 h-4 text-muted-foreground" strokeWidth={1.7} />}
        <input
          {...rest}
          data-testid={testid}
          className="flex-1 bg-transparent outline-none text-[17px] placeholder:text-muted-foreground/60"
          required
        />
      </div>
    </label>
  );
}

function PasswordField({ label, icon: Icon, testid, visible, onToggleVisible, ...rest }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium">
        {label}
      </span>
      <div className="mt-1 flex items-center gap-2 border-b-2 border-border focus-within:border-primary transition-colors py-2">
        {Icon && <Icon className="w-4 h-4 text-muted-foreground" strokeWidth={1.7} />}
        <input
          {...rest}
          type={visible ? "text" : "password"}
          data-testid={testid}
          className="flex-1 bg-transparent outline-none text-[17px] placeholder:text-muted-foreground/60"
          required
        />
        <button
          type="button"
          data-testid={`${testid}-toggle`}
          onClick={onToggleVisible}
          aria-label={visible ? "Hide password" : "Show password"}
          className="tap p-1 text-muted-foreground hover:text-foreground"
        >
          {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </label>
  );
}

function RememberMeCheckbox({ checked, onChange }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      data-testid="signin-remember-me"
      onClick={onChange}
      className="flex items-center gap-2 py-1 tap text-left"
    >
      <span
        className={[
          "w-5 h-5 rounded-md border-2 inline-flex items-center justify-center transition-colors",
          checked ? "bg-foreground border-foreground" : "border-border bg-transparent",
        ].join(" ")}
      >
        {checked && <Check className="w-3.5 h-3.5 text-background" strokeWidth={3} />}
      </span>
      <span className="text-sm text-muted-foreground">Remember me</span>
    </button>
  );
}

export { Link };
